<template>
    <transition name="fade">
        <div class="patient-current-data-summary">

            <h4>{{ $i18n.gt('HEADING_LIFESTYLE_RISK_FACTORS') }}</h4>
            <b-row :cols="$mq === 'sm' ? 1 : 5">
                <b-col class="text-center">
                    <label>{{ $i18n.gt('LABEL_INACTIVITY') }}</label>
                    <report-meter score-name="SCORE_SED">
                    </report-meter>
                </b-col>
                <b-col class="text-center">
                    <label>{{ $i18n.gt('LABEL_ACTIVITY') }}</label>
                    <report-meter score-name="SCORE_EXER">
                    </report-meter>
                </b-col>
                <b-col class="text-center">
                    <label>{{ $i18n.gt('LABEL_EMOTIONS') }}</label>
                    <report-meter score-name="SCORE_STRESS">
                    </report-meter>
                </b-col>
                <b-col class="text-center">
                    <label>{{ $i18n.gt('LABEL_DIET') }}</label>
                    <report-meter :score-name="dietScoreName">
                    </report-meter>
                </b-col>
                <b-col class="text-center">
                    <label>{{ $i18n.gt('LABEL_SLEEP') }}</label>
                    <report-meter score-name="SCORE_SLEEP">
                    </report-meter>
                </b-col>
            </b-row>

            <h4 class="mt-3">{{ $i18n.gt('HEADING_SOCIAL_FACTORS') }}</h4>
            <div class="indicator-lights indicator-lights--thirds">
                <report-indicator-light-mini class="score-trend-widget is-indicator-light"
                                             v-for="(scoreName, index) in indicatorLights" :key="index"
                                             :score-name="scoreName"
                                             :label-key="`LABEL_${scoreName}`"
                                             :show-always="false">
                </report-indicator-light-mini>
            </div>

            <h4 class="mt-3">{{ $i18n.gt('HEADING_MENTAL_HEALTH_SCREENING') }}</h4>
            <b-row :cols="$mq === 'sm' ? 1 : 4">
                <b-col class="text-center">
                    <label>{{ $i18n.gt('LABEL_DEPRESSION') }}</label>
                    <report-meter score-name="PHQ9_SCORE">
                    </report-meter>
                </b-col>
                <b-col class="text-center">
                    <label>{{ $i18n.gt('LABEL_ANXIETY') }}</label>
                    <report-meter score-name="GAD7_SCORE">
                    </report-meter>
                </b-col>
                <b-col class="text-center">
                    <label>{{ $i18n.gt('LABEL_ALCOHOL') }}</label>
                    <report-meter score-name="AUDIT_SCORE">
                    </report-meter>
                </b-col>
                <b-col class="text-center">
                    <label>{{ $i18n.gt('LABEL_OPIOIDS') }}</label>
                    <report-meter score-name="ORT_SCORE">
                    </report-meter>
                </b-col>
            </b-row>

            <h4 class="mt-3">{{ $i18n.gt('HEADING_OVERALL_RISK') }}</h4>
            <b-row :cols="$mq === 'sm' ? 1 : 4">
                <b-col class="text-center">
                    <label>{{ $i18n.gt('LABEL_FRAMINGHAM') }}</label>
                    <report-score-framingham></report-score-framingham>
                </b-col>
                <b-col class="text-center">
                    <label>{{ $i18n.gt('LABEL_ASCVD') }}</label>
                    <report-score-acc-aha></report-score-acc-aha>
                </b-col>
                <b-col class="text-center">
                    <label>{{ $i18n.gt('LABEL_MEDICAL_HISTORY') }}</label>
                    <report-meter score-name="SCORE_PMH">
                    </report-meter>
                </b-col>
                <b-col class="text-center">
                    <label>{{ $i18n.gt('LABEL_OVERALL_RISK') }}</label>
                    <report-meter score-name="TOTAL_SCORE">
                    </report-meter>
                </b-col>
            </b-row>

        </div>
    </transition>
</template>

<!-- ----------------------------------------------------------------------- -->

<script>
import Vue from 'vue'
import ReportMixin from '@/providers/ReportMixin'

/**
 * Component to show most recent patient responses/scores, for use by Practitioner
 * to select recommended resources and create a SurveyNote
 */
export default Vue.extend({
    name: 'PatientCurrentDataSummary',
    mixins: [ReportMixin],
    data() {
        return {
            indicatorLights: [
                'OK_FEELS_WELL',
                'OK_HRQOL',
                'OK_LITERACY',
                'OK_SECURITY',
                'OK_SOCIAL_SUPPORT',
                'OK_HEALTH_LITERACY',
                'OK_FINANCES',
                'OK_FOOD_SECURITY',
                'OK_COMMUNITY',
                'OK_ADLS',
                'OK_POVERTY',
                'OK_TRANSPORTATION',
                'OK_CENTERED'
            ]
        }
    },
    computed: {
        useDietId() {
            return this.$store.state.patientParticipantProviderUsesDietId
        },
        // show the appropriate diet score, depending on setting for practice
        dietScoreName() {
            return this.useDietId ? 'SCORE_DIETID' : 'SCORE_DIET'
        }
    }
})
</script>

<style lang="scss">

.indicator-lights--thirds {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;

    .report-indicator-light-mini {
        width: 32%;
    }
}

.patient-current-data-summary {

    section {
        .report-widget, .report-widget-plan {
            margin-bottom: 1em;
        }
    }

    .card {
        height: 100%;
    }
}

.patient-current-data-report__slider {
    display: inline-block;
    width: 200px;
    margin: 0 10px;
    @media (max-width: 767px) {
        margin: 10px;
    }

    .noUi-connect {
        background-color: var(--btn-bg);
    }
}

.patient-current-data-report__social {

    .badge {
        @media (max-width: 767px) {
            font-size: .65em;
        }
    }

    .card-body {
        min-height: 50px;
    }

    .report-widget__timestamp {
        bottom: .5em;
        right: .5em;
    }
}

.patient-current-data-report__plan {
    ul {
        padding-top: .5em;
    }

    li {
        line-height: 1.3;
    }

    .card-body {
        padding-bottom: 0;
    }
}

.patient-current-data-report__lifestyle, .patient-current-data-report__biometric {
    .highcharts-container {
        margin: 0 auto;
    }

    @media (max-width: 575px) {
        .report-widget {
            padding-left: 10px;
            padding-right: 10px;

            &:nth-child(odd) {
                padding-right: 5px;
            }

            &:nth-child(even) {
                padding-left: 5px;
            }
        }
    }
    @media (min-width: 576px) {
        .card-body {
            padding: 1em .5em;
        }
    }
}

</style>
